import * as React from 'react'

import { Avatar, Box, BoxList, Button, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import type { ApprovalFlowArgs, ApproverArgs } from 'modules/approvals/lib/approvalFlows'
import { roleInitials, roleName } from 'modules/approvals/lib/roles'

import AddApproverModal from './AddApproverModal'

type StepArgs = ApprovalFlowArgs['steps'][number]

interface Props {
  onSubmit: (step: StepArgs) => void
  onClose: () => void
  step: StepArgs
}

interface DetailProps {
  approver: ApproverArgs
  position: number
  canDelete: boolean
  onUpdate: (approver: ApproverArgs, position: number) => void
  onDelete: (approver: ApproverArgs) => void
}

const ApproverDetails = ({ approver, position, canDelete, onUpdate, onDelete }: DetailProps) => {
  const approverUser = approver.strategy === 'members' ? users.get(approver.accessId) : null

  const [showEditModal, setShowEditModal] = React.useState(false)

  return (
    <>
      <BoxList.Item>
        <Box justifyContent="spaceBetween" flexDirection="row" width="full">
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Box flexDirection="row" gap="s16" alignItems="center" justifyContent="spaceBetween">
              {approverUser ? (
                <UserAvatar user={approverUser} size="s32" />
              ) : (
                <Avatar size="s32" label={roleInitials(approver.strategy)} />
              )}
            </Box>

            <Text.Normal weight="medium">
              {(approverUser &&
                getFullDisplayName({
                  firstName: approverUser.attributes.first_name,
                  lastName: approverUser.attributes.last_name,
                })) ||
                roleName(approver.strategy)}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" gap="s8">
            <Button type="secondary" onClick={() => setShowEditModal(true)} iconName="pencil" />
            <Button
              disabled={!canDelete}
              type="secondary"
              onClick={() => onDelete(approver)}
              iconName="delete"
            />
          </Box>
        </Box>
      </BoxList.Item>
      {showEditModal && (
        <AddApproverModal
          approver={approver}
          onClose={() => setShowEditModal(false)}
          addApprover={(updatedApprover) => onUpdate(updatedApprover, position)}
        />
      )}
    </>
  )
}

const BulkApproversUpdateModal = ({ onSubmit, onClose, step }: Props) => {
  const [stepData, setStepData] = React.useState<StepArgs>(step)
  const canDelete = stepData.approvers.length > 1
  const onSave = () => {
    onSubmit(stepData)
    onClose()
  }

  const onCancel = () => {
    onClose()
  }

  const onUpdate = (updatedApprover: ApproverArgs, position: number) => {
    const approvers = stepData.approvers.map((approver, index) =>
      (approver.id && approver.id === updatedApprover.id) || index === position
        ? updatedApprover
        : approver
    )
    setStepData({ ...stepData, approvers })
  }

  const onDelete = (approver: ApproverArgs) => {
    const approvers = stepData.approvers.filter((a) => a !== approver)
    const approverIdsToBeDeleted = approver.id
      ? [...(stepData.approverIdsToBeDeleted ?? []), approver.id]
      : stepData.approverIdsToBeDeleted
    setStepData({ ...stepData, approvers, approverIdsToBeDeleted })
  }

  return (
    <Modal
      illustration="add-block"
      title={i18n.t('settings.approvals.approval_level.modify_approver_modal.title')}
      description={i18n.t('settings.approvals.approval_level.modify_approver_modal.description')}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onCancel}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={onSave}>{i18n.t('common.save')}</Button>
        </>
      }
    >
      <Box flexDirection="column" gap="s16">
        <BoxList>
          {stepData.approvers.map((approver, index) => (
            <ApproverDetails
              approver={approver}
              position={index}
              canDelete={canDelete}
              key={index}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
        </BoxList>
      </Box>
    </Modal>
  )
}

export default BulkApproversUpdateModal
